import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface KeyRow {
  readonly keyId: string;
  readonly keyCode: string;
  readonly keyAlias: string;
  readonly keyPurpose: string;
  readonly keyState: string;
}

const PURPOSES = ['', 'DATA_ENCRYPTION', 'ENVELOPE_MASTER', 'SIGNING', 'HMAC', 'TOKENIZATION'];
const STATES = ['', 'ACTIVE', 'DISABLED', 'DESTROYED'];

/**
 * GR L30.08 — KMS Key Catalogue (`/security/kms/keys`). Inventory of the GR
 * net-new `kms_key` store: managed keys with their purpose and lifecycle state.
 * Plaintext key material is never exposed here — only metadata. Filterable by
 * purpose and state.
 */
export function GrKmsKeysPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<KeyRow[]>([]);
  const [keyPurpose, setKeyPurpose] = useState('');
  const [keyState, setKeyState] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (keyPurpose) params.set('keyPurpose', keyPurpose);
    if (keyState) params.set('keyState', keyState);
    const qs = params.toString();
    apiRequest<{ data: KeyRow[] }>(`/v1/security/kms/keys${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, keyPurpose, keyState]);

  return (
    <main aria-label="security-kms-keys">
      <h1>{t('gr30.kms.title')}</h1>
      <div className="filters">
        <label>
          {t('gr30.kms.labelPurpose')}
          <select aria-label="filter-purpose" value={keyPurpose} onChange={(e) => setKeyPurpose(e.target.value)}>
            {PURPOSES.map((p) => <option key={p || 'all'} value={p}>{p || t('gr30.kms.all')}</option>)}
          </select>
        </label>
        <label>
          {t('gr30.kms.labelState')}
          <select aria-label="filter-state" value={keyState} onChange={(e) => setKeyState(e.target.value)}>
            {STATES.map((s) => <option key={s || 'all'} value={s}>{s || t('gr30.kms.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('gr30.kms.loading')}</p> : (
        <>
          <p aria-label="key-count">{t('gr30.kms.count', { count: String(rows.length) })}</p>
          {rows.length === 0 ? <p aria-label="empty">{t('gr30.kms.empty')}</p> : (
            <table aria-label="key-table">
              <thead><tr><th>{t('gr30.kms.colCode')}</th><th>{t('gr30.kms.colAlias')}</th><th>{t('gr30.kms.colPurpose')}</th><th>{t('gr30.kms.colState')}</th></tr></thead>
              <tbody>
                {rows.map((k) => (
                  <tr key={k.keyId}>
                    <td>{k.keyCode}</td><td>{k.keyAlias}</td><td>{k.keyPurpose}</td><td>{k.keyState}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </main>
  );
}
